// src/Hooks/useImportExcelManage.js

import { useEffect, useState } from 'react';
import { doc, collection, deleteDoc } from 'firebase/firestore';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { db, storage } from '../Firebase/firebase';

import { useImportExcelState } from './useImportExcelState';
import { useImportExcelParser } from './useImportExcelParser';
import { useImportExcelFirebase } from './useImportExcelFirebase';
import { useImportExcelReinstate } from './useImportExcelReinstate';
import { useImportExcelDocuments } from './useImportExcelDocuments';

const normalizeString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

const getDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth radius in metres
  const p1 = lat1 * (Math.PI / 180);
  const p2 = lat2 * (Math.PI / 180);
  const dp = (lat2 - lat1) * (Math.PI / 180);
  const dl = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dp / 2) * Math.sin(dp / 2) +
    Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) * Math.sin(dl / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const useImportExcelManage = ({ setIsDirty }) => {
  const stateStore = useImportExcelState();
  const parser = useImportExcelParser(stateStore);
  const firebase = useImportExcelFirebase(stateStore);
  const reinstate = useImportExcelReinstate(stateStore);

  const { state, setters, flags, uiActions, refs } = stateStore;

  const docState = useImportExcelDocuments(
    setters.setParsedData,
    state.rowMappings,
    state.customRows,
  );

  const [saveTrigger, setSaveTrigger] = useState(0);

  const effectiveHasData = flags.hasData || (state.columns && state.columns.length > 0);

  // --- RETROACTIVE COORDINATE EXTRACTION ---
  // When mappings change on Page 4, hydrate row.lat & row.lng so the drawer on Page 2 works
  useEffect(() => {
    if (!state.parsedData || state.parsedData.length === 0) return;

    let needsUpdate = false;
    const updatedData = state.parsedData.map((row) => {
      let newLat = row.lat;
      let newLng = row.lng;

      const posColKey = Object.keys(state.rowMappings || {}).find(
        (k) =>
          state.rowMappings[k] === 'POSITION (LAT, LNG)' ||
          state.rowMappings[k] === 'POSITION',
      );

      if (posColKey) {
        let posColName = null;
        const cRow = state.customRows?.find((r) => r.id === posColKey);
        if (cRow && cRow.name !== '-') posColName = cRow.name;
        else if (posColKey.startsWith('prop_')) posColName = posColKey.substring(5);

        if (posColName) {
          const actualKey = Object.keys(row).find(
            (k) => normalizeString(k) === normalizeString(posColName),
          );
          if (actualKey && row[actualKey] !== undefined && row[actualKey] !== null) {
            const matches = String(row[actualKey]).match(/-?\d+(\.\d+)?/g);
            if (matches && matches.length >= 2) {
              newLat = matches[0];
              newLng = matches[1];
            }
          }
        }
      }

      const latColKey = Object.keys(state.rowMappings || {}).find(
        (k) => state.rowMappings[k] === 'LATITUDE' || state.rowMappings[k] === 'LAT',
      );
      const lngColKey = Object.keys(state.rowMappings || {}).find(
        (k) => state.rowMappings[k] === 'LONGITUDE' || state.rowMappings[k] === 'LNG',
      );

      if (latColKey) {
        let latColName = null;
        const cRow = state.customRows?.find((r) => r.id === latColKey);
        if (cRow && cRow.name !== '-') latColName = cRow.name;
        else if (latColKey.startsWith('prop_')) latColName = latColKey.substring(5);

        if (latColName) {
          const actualKey = Object.keys(row).find(
            (k) => normalizeString(k) === normalizeString(latColName),
          );
          if (actualKey && row[actualKey] !== undefined && row[actualKey] !== null) {
            const match = String(row[actualKey]).match(/-?\d+(\.\d+)?/);
            if (match) newLat = match[0];
          }
        }
      }

      if (lngColKey) {
        let lngColName = null;
        const cRow = state.customRows?.find((r) => r.id === lngColKey);
        if (cRow && cRow.name !== '-') lngColName = cRow.name;
        else if (lngColKey.startsWith('prop_')) lngColName = lngColKey.substring(5);

        if (lngColName) {
          const actualKey = Object.keys(row).find(
            (k) => normalizeString(k) === normalizeString(lngColName),
          );
          if (actualKey && row[actualKey] !== undefined && row[actualKey] !== null) {
            const match = String(row[actualKey]).match(/-?\d+(\.\d+)?/);
            if (match) newLng = match[0];
          }
        }
      }

      const parsedNewLat =
        newLat !== undefined && newLat !== null ? parseFloat(newLat) : undefined;
      const parsedNewLng =
        newLng !== undefined && newLng !== null ? parseFloat(newLng) : undefined;
      const parsedOldLat =
        row.lat !== undefined && row.lat !== null ? parseFloat(row.lat) : undefined;
      const parsedOldLng =
        row.lng !== undefined && row.lng !== null ? parseFloat(row.lng) : undefined;

      if (
        parsedNewLat !== undefined &&
        !isNaN(parsedNewLat) &&
        parsedNewLng !== undefined &&
        !isNaN(parsedNewLng)
      ) {
        if (parsedOldLat !== parsedNewLat || parsedOldLng !== parsedNewLng) {
          needsUpdate = true;
          return { ...row, lat: parsedNewLat, lng: parsedNewLng };
        }
      }

      return row;
    });

    if (needsUpdate) {
      setters.setParsedData(updatedData);
    }
  }, [state.rowMappings, state.customRows, state.parsedData, setters]);

  useEffect(() => {
    if (saveTrigger === 1) {
      const timer = setTimeout(() => {
        firebase.executeSave(false);
        setSaveTrigger(0);
      }, 150);
      return () => clearTimeout(timer);
    } else if (saveTrigger === 2) {
      const timer = setTimeout(() => {
        firebase.executeSave(true);
        setSaveTrigger(0);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [saveTrigger, firebase]);

  useEffect(() => {
    if (!state.isMenuDirty) {
      setters.setLocalMenus(state.savedMenus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.savedMenus, state.isMenuDirty]);

  // Robustly push the dirty status up to the parent panel
  useEffect(() => {
    if (setIsDirty) setIsDirty(effectiveHasData);
  }, [effectiveHasData, setIsDirty]);

  useEffect(() => {
    if (
      state.pendingMissingColsCheck &&
      !state.isPhotoFlowActive &&
      state.columns.length > 0
    ) {
      setters.setPendingMissingColsCheck(false);

      const expectedCols = state.customRows
        .filter((r) => state.rowMappings[r.id] && r.name !== '-')
        .map((r) => r.name);

      const templatePhotoColKey = Object.keys(state.rowMappings).find(
        (k) => state.rowMappings[k] === 'PHOTOS',
      );

      let templatePhotoColName = 'Photos';
      if (templatePhotoColKey) {
        const cRow = state.customRows.find((r) => r.id === templatePhotoColKey);
        if (cRow && cRow.name !== '-') templatePhotoColName = cRow.name;
      }

      let missingCols = expectedCols.filter((col) => {
        const normCol = normalizeString(col);
        return !state.columns.some((c) => normalizeString(c) === normCol);
      });

      if (state.stagedFiles && state.stagedFiles.length > 0) {
        const normPhoto = normalizeString(templatePhotoColName);
        missingCols = missingCols.filter((col) => normalizeString(col) !== normPhoto);
      }

      if (missingCols.length > 0) {
        setters.setMissingColumnsDialog({
          open: true,
          missingCols,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.isPhotoFlowActive,
    state.pendingMissingColsCheck,
    state.columns,
    state.rowMappings,
    state.stagedFiles,
    state.customRows,
  ]);

  const applyMenuSelection = (menuId) => {
    if (refs?.isTemplateFromReinstateRef) {
      refs.isTemplateFromReinstateRef.current = false;
    }

    if (menuId) {
      const menu = state.localMenus.find((m) => m.id === menuId);

      if (menu) {
        setters.setSelectedMenuId(menu.id);

        const newMappings = {};
        const newSubMappings = {};
        const newStaticValues = {};
        const newCustomRows = [];

        if (menu.mappings && Array.isArray(menu.mappings)) {
          menu.mappings.forEach((m) => {
            if (m.assignedTo) newMappings[m.id] = m.assignedTo;
            if (m.selection) newSubMappings[m.id] = m.selection;
            if (m.value) newStaticValues[m.id] = m.value;
            newCustomRows.push({ id: m.id, name: m.property, isCustom: true });
          });
        } else {
          if (menu.rowMappings) {
            Object.assign(newMappings, menu.rowMappings);
            Object.keys(menu.rowMappings).forEach((key) => {
              if (key.startsWith('prop_')) {
                newCustomRows.push({ id: key, name: key.substring(5), isCustom: true });
              }
            });
          }
          if (menu.rowSubMappings) Object.assign(newSubMappings, menu.rowSubMappings);
          if (menu.rowStaticValues) Object.assign(newStaticValues, menu.rowStaticValues);
          if (menu.customRows) {
            menu.customRows.forEach((cr) => {
              if (!newCustomRows.some((r) => r.id === cr.id)) {
                newCustomRows.push(cr);
              }
            });
          }
        }

        setters.setRowMappings(newMappings);
        setters.setRowSubMappings(newSubMappings);
        setters.setRowStaticValues(newStaticValues);
        setters.setCustomRows(newCustomRows);

        const newTemplateColNames = newCustomRows
          .filter((r) => newMappings[r.id] && r.name !== '-')
          .map((r) => r.name);

        setters.setColumns((prevCols) => {
          if (!state.parsedData || state.parsedData.length === 0) {
            return prevCols;
          }

          const updatedCols = [...prevCols];
          let colsAdded = false;

          newTemplateColNames.forEach((colName) => {
            const exists = updatedCols.some(
              (c) => normalizeString(c) === normalizeString(colName),
            );
            if (!exists) {
              updatedCols.push(colName);
              colsAdded = true;
            }
          });

          return colsAdded ? updatedCols : prevCols;
        });
      }
    } else {
      setters.setSelectedMenuId(null);
      setters.setRowMappings({});
      setters.setRowSubMappings({});
      setters.setRowStaticValues({});
      setters.setCustomRows([]);
      setters.setIsMenuDirty(false);
    }
  };

  const handleSelectMenu = (menuId) => {
    applyMenuSelection(menuId);
  };

  const handleMenuTitleChange = (id, newTitle) => {
    if (!id) return;
    setters.setLocalMenus((prev) =>
      prev.map((m) => (m.id === id ? { ...m, title: newTitle } : m)),
    );
    setters.setIsMenuDirty(true);
  };

  const handleAddNewMenu = () => {
    if (refs?.isTemplateFromReinstateRef) {
      refs.isTemplateFromReinstateRef.current = false;
    }

    const newId = doc(collection(db, 'excelImportMenus')).id;
    setters.setLocalMenus((prev) => [
      {
        id: newId,
        title: '',
        isNew: true,
        mappings: [],
        updatedAt: new Date().toISOString(),
      },
      ...prev,
    ]);
    setters.setSelectedMenuId(newId);

    setters.setRowMappings({});
    setters.setRowSubMappings({});
    setters.setRowStaticValues({});
    setters.setCustomRows([]);

    setters.setIsMenuDirty(true);
  };

  const handleCopyMenu = () => {
    if (!state.selectedMenuId) return;
    const menuToCopy = state.localMenus.find((m) => m.id === state.selectedMenuId);
    if (menuToCopy) {
      let mappingsToCopy = menuToCopy.mappings || [];
      if (state.selectedMenuId === menuToCopy.id) {
        const activeMappings = [];
        const processedKeys = new Set();

        state.customRows.forEach((cRow) => {
          processedKeys.add(cRow.id);
          activeMappings.push({
            id: cRow.id,
            property: cRow.name,
            assignedTo: state.rowMappings[cRow.id] || '',
            selection: state.rowSubMappings[cRow.id] || '',
            value: state.rowStaticValues[cRow.id] || '',
            isCustom: true,
          });
        });

        Object.keys(state.rowMappings).forEach((key) => {
          if (!processedKeys.has(key)) {
            activeMappings.push({
              id: key,
              property: key.startsWith('prop_') ? key.substring(5) : '-',
              assignedTo: state.rowMappings[key] || '',
              selection: state.rowSubMappings[key] || '',
              value: state.rowStaticValues[key] || '',
              isCustom: true,
            });
          }
        });

        mappingsToCopy = activeMappings;
      }

      setters.setCopiedMenuTemplate({
        ...menuToCopy,
        mappings: mappingsToCopy,
      });
      setters.setAlertState({
        open: true,
        message: 'Template copied to clipboard',
        severity: 'success',
      });
    }
  };

  const handleAddDataRow = () => {
    const newRow = {
      id: `manual_row_${Date.now()}`,
      photoMatch: 'NO_MATCH',
      matchedFiles: [],
      documents: [],
      _manuallyAssigned: {},
      _localMappings: {},
    };

    state.columns.forEach((col) => {
      newRow[col] = '';
    });

    setters.setParsedData((prev) => [...prev, newRow]);
  };

  const handleDeleteColumnClick = (colName) => {
    setters.setConfirmDialog({
      open: true,
      type: 'DELETE_COLUMN',
      colName: colName,
      message: 'This will delete this property permanently. Do you wish to continue?',
      confirmText: 'Tick',
      cancelText: 'Cancel',
      severity: 'warning',
    });
  };

  const executeCancel = () => {
    if (refs?.isTemplateFromReinstateRef) {
      refs.isTemplateFromReinstateRef.current = false;
    }

    if (state.parsedData && state.parsedData.length > 0) {
      state.parsedData.forEach((row) => {
        if (row.originalMarkerGroupID && !state.pendingReinstateBatchIds?.length) {
          listAll(ref(storage, `documents/${row.originalMarkerGroupID}`))
            .then((res) => {
              res.items.forEach((item) => deleteObject(item).catch(() => {}));
            })
            .catch(() => {});
        }
      });
    }

    setters.setParsedData([]);
    setters.setColumns([]);
    setters.setCustomRows([]);
    setters.setSelectedCustomIds([]);
    setters.setSelectedRowIds([]);
    setters.setCheckedRightIds([]);
    setters.setRowMappings({});
    setters.setRowSubMappings({});
    setters.setRowStaticValues({});
    setters.setStagedFiles([]);

    docState.resetDocuments();

    setters.setManualMatchRowId(null);
    setters.setPendingNextColumn(null);
    setters.setUserZoomLevel(null);
    setters.setResolveCoordsState({
      open: false,
      rowId: null,
      searchTarget: '',
      actualTitle: '',
      geoSuffix: '',
    });
    setters.setSelectedHistoryIds([]);
    setters.setSuccessfullyImportedIds([]);
    setters.setSelectedMenuId(null);
    setters.setIsMenuDirty(false);
    setters.setTriggerDuplicateDelete(false);
    setters.setViewMode('MENU_VIEW');
    setters.setLocalMenus(state.savedMenus);
    setters.setIsPhotoFlowActive(false);
    setters.setPendingMissingColsCheck(false);
    setters.setSessionBatches({});
    setters.setMissingColumnsDialog({ open: false, missingCols: [] });
    setters.setConfirmDialog({ ...state.confirmDialog, open: false });
    setters.setInvalidMatchState({
      open: false,
      rowId: null,
      colName: null,
      cellValue: '',
      type: null,
    });
    setters.setPendingReinstateBatchIds([]);
    setters.setPendingDuplicateDeletions([]);
    setters.setPendingDocumentDeletions([]);
  };

  const handleCancelClick = () => {
    if (state.viewMode === 'SETTINGS_VIEW' && state.isMenuDirty) {
      setters.setConfirmDialog({
        open: true,
        type: 'CANCEL_SETTINGS',
        message:
          'Cancel will discard any unsaved changes to this template. Are you sure?',
        confirmText: 'Yes',
        cancelText: 'No',
        severity: 'warning',
      });
    } else {
      setters.setConfirmDialog({
        open: true,
        type: 'CANCEL',
        message: 'Cancel will discard your data and session. Are you sure?',
        confirmText: 'Yes',
        cancelText: 'No',
        severity: 'warning',
      });
    }
  };

  const handleCancelBulkCoords = () => {
    refs.cancelProcessRef.current = true;
  };

  const handleReinstateClick = () => {
    if (state.selectedHistoryIds.length === 0) {
      setters.setAlertState({
        open: true,
        message: 'Please select at least one batch to reinstate.',
        severity: 'warning',
      });
      return;
    }
    reinstate.executeReinstateBatch();
  };

  const handleDeleteClick = () => {
    if (state.viewMode === 'HISTORY_VIEW') {
      setters.setShowHistoryDeleteConfirm(true);
    } else if (state.viewMode === 'SETTINGS_VIEW') {
      setters.setConfirmDialog({
        open: true,
        type: 'DELETE_SETTINGS',
        message: 'This action will permanently delete the selected items. Are you sure?',
        confirmText: 'Yes',
        cancelText: 'No',
        severity: 'warning',
      });
    } else if (state.viewMode === 'MENU_VIEW') {
      if (state.selectedMenuId) {
        setters.setConfirmDialog({
          open: true,
          type: 'DELETE_MENU',
          message: 'This will permanently delete the selected template. Are you sure?',
          confirmText: 'Yes',
          cancelText: 'No',
          severity: 'warning',
        });
      }
    } else if (state.viewMode === 'DATA_VIEW') {
      if (state.selectedRowIds.length > 0) {
        setters.setConfirmDialog({
          open: true,
          type: 'DELETE_ROWS',
          message:
            'Do you want to permanently delete this marker and all its information?',
          confirmText: 'OK',
          cancelText: 'Cancel',
          severity: 'warning',
        });
      } else {
        setters.setConfirmDialog({
          open: true,
          type: 'DELETE_ALL',
          message: 'This action will clear all imported data. Are you sure?',
          confirmText: 'Yes',
          cancelText: 'No',
          severity: 'warning',
        });
      }
    } else if (state.viewMode === 'DUPLICATE_CHECK_VIEW') {
      if (state.selectedRowIds.length > 0 || state.checkedRightIds.length > 0) {
        setters.setTriggerDuplicateDelete(true);
      }
    }
  };

  const executeDeleteSettingsOrData = () => {
    if (state.confirmDialog.type === 'SELECT_TEMPLATE_FOR_REINSTATE') {
      setters.setConfirmDialog({
        ...state.confirmDialog,
        open: false,
        type: null,
        onConfirm: null,
      });
      setters.setViewMode('MENU_VIEW');
    } else if (state.confirmDialog.type === 'CONFIRM_TEMPLATE_FOR_REINSTATE') {
      setters.setConfirmDialog({
        ...state.confirmDialog,
        open: false,
        type: null,
        onConfirm: null,
      });
      reinstate.executeReinstateBatch(true);
    } else if (state.confirmDialog.type === 'CANCEL') {
      executeCancel();
    } else if (state.confirmDialog.type === 'CANCEL_SETTINGS') {
      const originalMenu = state.savedMenus.find((m) => m.id === state.selectedMenuId);
      if (!originalMenu) {
        setters.setLocalMenus(state.savedMenus);
        setters.setSelectedMenuId(null);
        setters.setRowMappings({});
        setters.setRowSubMappings({});
        setters.setRowStaticValues({});
        setters.setCustomRows([]);
        setters.setViewMode('MENU_VIEW');
      } else {
        setters.setLocalMenus(state.savedMenus);
        const newMappings = {};
        const newSubMappings = {};
        const newStaticValues = {};
        const newCustomRows = [];

        if (originalMenu.mappings && Array.isArray(originalMenu.mappings)) {
          originalMenu.mappings.forEach((m) => {
            if (m.assignedTo) newMappings[m.id] = m.assignedTo;
            if (m.selection) newSubMappings[m.id] = m.selection;
            if (m.value) newStaticValues[m.id] = m.value;
            newCustomRows.push({ id: m.id, name: m.property, isCustom: true });
          });
        } else {
          if (originalMenu.rowMappings) {
            Object.assign(newMappings, originalMenu.rowMappings);
            Object.keys(originalMenu.rowMappings).forEach((key) => {
              if (key.startsWith('prop_')) {
                newCustomRows.push({ id: key, name: key.substring(5), isCustom: true });
              }
            });
          }
          if (originalMenu.rowSubMappings)
            Object.assign(newSubMappings, originalMenu.rowSubMappings);
          if (originalMenu.rowStaticValues)
            Object.assign(newStaticValues, originalMenu.rowStaticValues);
          if (originalMenu.customRows) {
            originalMenu.customRows.forEach((cr) => {
              if (!newCustomRows.some((r) => r.id === cr.id)) newCustomRows.push(cr);
            });
          }
        }
        setters.setRowMappings(newMappings);
        setters.setRowSubMappings(newSubMappings);
        setters.setRowStaticValues(newStaticValues);
        setters.setCustomRows(newCustomRows);
      }
      setters.setIsMenuDirty(false);
      setters.setConfirmDialog({ ...state.confirmDialog, open: false });
    } else if (state.confirmDialog.type === 'PROCEED_WITH_INVALID') {
      setters.setConfirmDialog({ ...state.confirmDialog, open: false });
      setters.setUploadProgress({ current: 0, total: 0 });
      setters.setIsProcessing(true);
      setSaveTrigger(2);
    } else if (state.confirmDialog.type === 'ALL_IMPORTED') {
      setters.setParsedData([]);
      setters.setColumns([]);
      setters.setSelectedRowIds([]);
      setters.setCheckedRightIds([]);
      setters.setStagedFiles([]);
      docState.resetDocuments();
      setters.setSuccessfullyImportedIds([]);
      setters.setSessionBatches({});
      setters.setManualMatchRowId(null);
      setters.setPendingNextColumn(null);
      setters.setResolveCoordsState({
        open: false,
        rowId: null,
        searchTarget: '',
        actualTitle: '',
        geoSuffix: '',
      });
      setters.setInvalidMatchState({
        open: false,
        rowId: null,
        colName: null,
        cellValue: '',
        type: null,
      });
      setters.setConfirmDialog({ ...state.confirmDialog, open: false });
    } else if (state.confirmDialog.type === 'DELETE_SETTINGS') {
      setters.setCustomRows((prev) =>
        prev.filter((row) => !state.selectedCustomIds.includes(row.id)),
      );
      setters.setRowMappings((prev) => {
        const next = { ...prev };
        state.selectedCustomIds.forEach((id) => delete next[id]);
        return next;
      });
      setters.setRowSubMappings((prev) => {
        const next = { ...prev };
        state.selectedCustomIds.forEach((id) => delete next[id]);
        return next;
      });
      setters.setRowStaticValues((prev) => {
        const next = { ...prev };
        state.selectedCustomIds.forEach((id) => delete next[id]);
        return next;
      });
      setters.setSelectedCustomIds([]);
      setters.setIsMenuDirty(true);
      setters.setConfirmDialog({ ...state.confirmDialog, open: false });
    } else if (state.confirmDialog.type === 'DELETE_MENU') {
      if (state.selectedMenuId) {
        const menuToDelete = state.localMenus.find((m) => m.id === state.selectedMenuId);
        if (menuToDelete && menuToDelete.isNew) {
          setters.setLocalMenus((prev) =>
            prev.filter((m) => m.id !== state.selectedMenuId),
          );
          setters.setSelectedMenuId(null);
          setters.setRowMappings({});
          setters.setRowSubMappings({});
          setters.setRowStaticValues({});
          setters.setCustomRows([]);
          setters.setConfirmDialog({ ...state.confirmDialog, open: false });
          setters.setIsMenuDirty(false);
        } else {
          deleteDoc(doc(db, 'excelImportMenus', state.selectedMenuId))
            .then(() => {
              setters.setSelectedMenuId(null);
              setters.setRowMappings({});
              setters.setRowSubMappings({});
              setters.setRowStaticValues({});
              setters.setCustomRows([]);
              setters.setConfirmDialog({ ...state.confirmDialog, open: false });
              setters.setIsMenuDirty(false);
            })
            .catch((err) => {
              setters.setConfirmDialog({ ...state.confirmDialog, open: false });
              setters.setAlertState({
                open: true,
                message: err.message,
                severity: 'error',
              });
            });
        }
      } else {
        setters.setConfirmDialog({ ...state.confirmDialog, open: false });
      }
    } else if (state.confirmDialog.type === 'DELETE_ROWS') {
      const rowsToDelete = state.parsedData.filter((row) =>
        state.selectedRowIds.includes(row.id),
      );

      const remainingRows = state.parsedData.filter(
        (row) => !state.selectedRowIds.includes(row.id),
      );

      if (remainingRows.length === 0) {
        executeCancel();
      } else {
        rowsToDelete.forEach((row) => {
          if (row.originalMarkerGroupID && !state.pendingReinstateBatchIds?.length) {
            listAll(ref(storage, `documents/${row.originalMarkerGroupID}`))
              .then((res) => {
                res.items.forEach((item) => deleteObject(item).catch(() => {}));
              })
              .catch(() => {});
          }
        });

        setters.setParsedData(remainingRows);
        setters.setSelectedRowIds([]);
        setters.setConfirmDialog({ ...state.confirmDialog, open: false });
      }
    } else if (state.confirmDialog.type === 'DELETE_ALL') {
      executeCancel();
    } else if (state.confirmDialog.type === 'DELETE_COLUMN') {
      const colToDelete = state.confirmDialog.colName;
      const normColToDelete = normalizeString(colToDelete);

      setters.setColumns((prev) =>
        prev.filter((c) => normalizeString(c) !== normColToDelete),
      );
      setters.setParsedData((prev) =>
        prev.map((row) => {
          const newRow = { ...row };
          const actualKey = Object.keys(newRow).find(
            (k) => normalizeString(k) === normColToDelete,
          );
          if (actualKey) delete newRow[actualKey];
          return newRow;
        }),
      );
      setters.setConfirmDialog({ ...state.confirmDialog, open: false, colName: null });
    }
  };

  const handlePublishClick = () => setters.setShowPublishConfirm1(true);

  const handleSaveRequest = () => {
    let rowsToProcess = [];
    if (state.selectedRowIds.length > 0) {
      rowsToProcess = state.parsedData.filter(
        (row) =>
          state.selectedRowIds.includes(row.id) &&
          !state.successfullyImportedIds.includes(row.id),
      );
    } else {
      rowsToProcess = state.parsedData.filter(
        (row) => !state.successfullyImportedIds.includes(row.id),
      );
    }

    if (rowsToProcess.length === 0) {
      setters.setAlertState({
        open: true,
        message: 'No un-imported data selected.',
        severity: 'warning',
      });
      return;
    }

    const existingMarkers = window.__markerArray || [];
    let hasDuplicates = false;
    const DUPLICATE_RADIUS_METERS = 10;

    // Pre-extract coordinates for all rows being processed so we can check both Firebase and internal batch duplicates
    const extractedRowsCoords = rowsToProcess.map((row) => {
      let latVal =
        row['lat'] ??
        row['Lat'] ??
        row['LAT'] ??
        row['latitude'] ??
        row['Latitude'] ??
        row['LATITUDE'];
      let lngVal =
        row['lng'] ??
        row['Lng'] ??
        row['LNG'] ??
        row['longitude'] ??
        row['Longitude'] ??
        row['LONGITUDE'] ??
        row['Lon'] ??
        row['lon'];

      if (latVal == null || lngVal == null) {
        const posColKey = Object.keys(state.rowMappings || {}).find(
          (k) =>
            state.rowMappings[k] === 'POSITION (LAT, LNG)' ||
            state.rowMappings[k] === 'POSITION',
        );
        if (posColKey) {
          let colName = posColKey;
          const cRow = state.customRows?.find((r) => r.id === posColKey);
          if (cRow && cRow.name !== '-') colName = cRow.name;
          else if (posColKey.startsWith('prop_')) colName = posColKey.substring(5);

          const actualKey = Object.keys(row).find(
            (k) => normalizeString(k) === normalizeString(colName),
          );
          if (actualKey && row[actualKey] !== undefined && row[actualKey] !== null) {
            const matches = String(row[actualKey]).match(/-?\d+(\.\d+)?/g);
            if (matches && matches.length >= 2) {
              latVal = matches[0];
              lngVal = matches[1];
            }
          }
        }
      }

      return {
        id: row.id,
        parsedLat: parseFloat(latVal),
        parsedLng: parseFloat(lngVal),
      };
    });

    for (let i = 0; i < extractedRowsCoords.length; i++) {
      const { parsedLat, parsedLng } = extractedRowsCoords[i];

      if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
        // 1. Check against existing Firebase map markers
        for (const m of existingMarkers) {
          if (!m) continue;

          const mImportBatchId = m.importBatchId || m.data?.importBatchId;
          if (
            mImportBatchId &&
            state.pendingReinstateBatchIds?.includes(mImportBatchId)
          ) {
            continue;
          }

          const mId = m.id || m.data?.id;
          if (
            mId &&
            state.pendingDuplicateDeletions?.some(
              (d) => d.id === mId || d.data?.id === mId,
            )
          ) {
            continue;
          }

          let mLat = null;
          let mLng = null;

          const markerInstance = m.marker || m;
          const dataSource = m.data || m;

          if (typeof markerInstance.getPosition === 'function') {
            const pos = markerInstance.getPosition();
            if (pos) {
              mLat = typeof pos.lat === 'function' ? pos.lat() : pos.lat;
              mLng = typeof pos.lng === 'function' ? pos.lng() : pos.lng;
            }
          }

          if (mLat == null || mLng == null) {
            if (dataSource.position && dataSource.position.lat !== undefined) {
              mLat =
                typeof dataSource.position.lat === 'function'
                  ? dataSource.position.lat()
                  : dataSource.position.lat;
              mLng =
                typeof dataSource.position.lng === 'function'
                  ? dataSource.position.lng()
                  : dataSource.position.lng;
            } else {
              mLat = dataSource.lat !== undefined ? dataSource.lat : dataSource.latitude;
              mLng = dataSource.lng !== undefined ? dataSource.lng : dataSource.longitude;
            }
          }

          const parsedMLat = parseFloat(mLat);
          const parsedMLng = parseFloat(mLng);

          if (!isNaN(parsedMLat) && !isNaN(parsedMLng)) {
            const dist = getDistanceInMeters(
              parsedLat,
              parsedLng,
              parsedMLat,
              parsedMLng,
            );
            if (dist <= DUPLICATE_RADIUS_METERS) {
              hasDuplicates = true;
              break;
            }
          }
        }

        // 2. Check against other rows in the current batch (Page 2 duplicates)
        if (!hasDuplicates) {
          for (let j = 0; j < extractedRowsCoords.length; j++) {
            if (i === j) continue;
            const other = extractedRowsCoords[j];
            if (!isNaN(other.parsedLat) && !isNaN(other.parsedLng)) {
              const dist = getDistanceInMeters(
                parsedLat,
                parsedLng,
                other.parsedLat,
                other.parsedLng,
              );
              if (dist <= DUPLICATE_RADIUS_METERS) {
                hasDuplicates = true;
                break;
              }
            }
          }
        }
      }
      if (hasDuplicates) break;
    }

    if (hasDuplicates) {
      setters.setAlertState({
        open: true,
        message: 'Duplicates found.  Check the next page',
        severity: 'warning',
      });
      return;
    }

    setters.setShowBatchFilenameDialog(true);
  };

  const confirmImportBatch = (filename) => {
    setters.setCurrentFilename(filename);
    setters.setShowBatchFilenameDialog(false);
    setters.setUploadProgress({ current: 0, total: 0 });
    setters.setIsProcessing(true);
    setSaveTrigger(1);
  };

  const handleManualMatch = (filesArray) => {
    const photoColName =
      state.columns.find(
        (c) => c.toLowerCase().includes('photo') || c.toLowerCase().includes('image'),
      ) || 'Photos';

    setters.setParsedData((prev) =>
      prev.map((row) => {
        if (row.id === state.manualMatchRowId) {
          return {
            ...row,
            photoMatch: filesArray && filesArray.length > 0 ? 'MATCH' : 'NO_MATCH',
            [photoColName]:
              filesArray && filesArray.length > 0
                ? filesArray.length === 1
                  ? filesArray[0].name
                  : `${filesArray.length} photos`
                : '',
            matchedFiles: filesArray || [],
            _manuallyAssigned: {
              ...(row._manuallyAssigned || {}),
              [photoColName]: filesArray && filesArray.length > 0,
            },
          };
        }
        return row;
      }),
    );
  };

  const handleDocumentMatch = (filesArray, explicitRowId = null) => {
    const targetRowId = explicitRowId || docState.documentMatchRowId;
    if (!targetRowId) return;

    const docColKey = Object.keys(state.rowMappings).find(
      (k) => state.rowMappings[k] === 'DOCUMENTS',
    );

    let docColName = 'Documents';
    if (docColKey) {
      const cr = state.customRows.find((r) => r.id === docColKey);
      if (cr && cr.name !== '-') docColName = cr.name;
      else if (docColKey.startsWith('prop_')) docColName = docColKey.substring(5);
    }

    setters.setParsedData((prev) =>
      prev.map((row) => {
        if (row.id === targetRowId) {
          return {
            ...row,
            [docColName]:
              filesArray && filesArray.length > 0
                ? filesArray.length === 1
                  ? filesArray[0].name || filesArray[0].fileName
                  : `${filesArray.length} documents`
                : '',
            documents: filesArray || [],
            _manuallyAssigned: {
              ...(row._manuallyAssigned || {}),
              [docColName]: filesArray && filesArray.length > 0,
            },
          };
        }
        return row;
      }),
    );
  };

  const deleteDocumentFromStorage = (url) => {
    if (!url) return;
    setters.setPendingDocumentDeletions((prev) => {
      if (prev && !prev.includes(url)) return [...prev, url];
      return prev || [url];
    });
  };

  const handleResolveCoordinates = async (
    rowId,
    newLat,
    newLng,
    applyToAll,
    newTargetTitle,
  ) => {
    const titleColKey = Object.keys(stateStore.state.rowMappings).find(
      (k) =>
        normalizeString(stateStore.state.rowMappings[k]) === normalizeString('INFO') &&
        normalizeString(stateStore.state.rowSubMappings[k]) === normalizeString('TITLE'),
    );
    let titleColName = null;
    if (titleColKey) {
      const cr = stateStore.state.customRows?.find((r) => r.id === titleColKey);
      titleColName =
        cr && cr.name !== '-'
          ? cr.name
          : titleColKey.startsWith('prop_')
            ? titleColKey.substring(5)
            : null;
    }
    if (!titleColName) {
      titleColName =
        stateStore.state.columns.find((c) => normalizeString(c) === 'title') ||
        (stateStore.state.columns.length > 0 ? stateStore.state.columns[0] : null);
    }

    const applyCoordsToRow = (row, nLat, nLng, nTitle) => {
      const updated = { ...row };

      if (nTitle && titleColName) {
        const exactTitleKey = Object.keys(row).find(
          (k) => normalizeString(k) === normalizeString(titleColName),
        );
        if (exactTitleKey) {
          updated[exactTitleKey] = nTitle;
        } else {
          updated[titleColName] = nTitle;
        }
      }

      const isLatValid =
        nLat !== null && nLat !== undefined && nLat !== '' && !isNaN(parseFloat(nLat));
      const isLngValid =
        nLng !== null && nLng !== undefined && nLng !== '' && !isNaN(parseFloat(nLng));

      if (isLatValid && isLngValid) {
        const formattedLat = parseFloat(nLat).toFixed(10);
        const formattedLng = parseFloat(nLng).toFixed(10);

        updated.lat = formattedLat;
        updated.lng = formattedLng;

        const posColKey = Object.keys(stateStore.state.rowMappings).find(
          (k) =>
            normalizeString(stateStore.state.rowMappings[k]) ===
            normalizeString('POSITION (LAT, LNG)'),
        );
        let posColName = null;
        if (posColKey) {
          const cRow = stateStore.state.customRows.find((r) => r.id === posColKey);
          if (cRow && cRow.name !== '-') {
            posColName = cRow.name;
          } else if (posColKey.startsWith('prop_')) {
            posColName = posColKey.substring(5);
          }
        }

        if (posColName && updated[posColName] !== undefined) {
          updated[posColName] = `${formattedLat}, ${formattedLng}`;
        } else {
          const latKeys = ['Latitude', 'latitude', 'Lat', 'lat', 'LAT', 'LATITUDE'];
          const lngKeys = [
            'Longitude',
            'longitude',
            'Lng',
            'lng',
            'LNG',
            'LONGITUDE',
            'Lon',
            'lon',
          ];
          latKeys.forEach((k) => {
            if (updated[k] !== undefined) updated[k] = formattedLat;
          });
          lngKeys.forEach((k) => {
            if (updated[k] !== undefined) updated[k] = formattedLng;
          });
        }

        updated._manuallyAssigned = {
          ...(updated._manuallyAssigned || {}),
          coords: true,
        };
      }
      return updated;
    };

    setters.setParsedData((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          return applyCoordsToRow(row, newLat, newLng, newTargetTitle);
        }
        return row;
      }),
    );

    if (stateStore.state.resolveCoordsState.rowId === rowId) {
      setters.setResolveCoordsState((prev) => {
        const finalTitle =
          newTargetTitle !== undefined ? newTargetTitle : prev.actualTitle;
        return {
          ...prev,
          actualTitle: finalTitle,
          searchTarget: finalTitle,
        };
      });
    }

    if (applyToAll && window.google && window.google.maps) {
      setters.setResolveCoordsState((prev) => ({ ...prev, isBulkGenerating: true }));

      const geocoder = new window.google.maps.Geocoder();
      let currentData = [...stateStore.state.parsedData];

      try {
        for (let i = 0; i < currentData.length; i++) {
          if (stateStore.refs.cancelProcessRef.current) break;

          const row = currentData[i];

          let hasCoords = false;
          if (row.lat !== undefined && row.lng !== undefined) hasCoords = true;
          else {
            const positionColKey = Object.keys(stateStore.state.rowMappings).find(
              (k) =>
                normalizeString(stateStore.state.rowMappings[k]) ===
                normalizeString('POSITION (LAT, LNG)'),
            );
            let posColName = null;
            if (positionColKey) {
              const cRow = stateStore.state.customRows.find(
                (r) => r.id === positionColKey,
              );
              if (cRow && cRow.name !== '-') posColName = cRow.name;
              else if (positionColKey.startsWith('prop_'))
                posColName = positionColKey.substring(5);
            }
            const cellValue = posColName ? row[posColName] : null;
            if (cellValue) {
              const matches = String(cellValue).match(/-?\d+(\.\d+)?/g);
              if (matches && matches.length >= 2) hasCoords = true;
            }
            if (!hasCoords) {
              const rawLat =
                row['Latitude'] ??
                row['latitude'] ??
                row['Lat'] ??
                row['lat'] ??
                row['LAT'] ??
                row['LATITUDE'];
              const rawLng =
                row['Longitude'] ??
                row['longitude'] ??
                row['Lng'] ??
                row['lng'] ??
                row['LNG'] ??
                row['LONGITUDE'] ??
                row['Lon'] ??
                row['lon'];
              if (rawLat !== undefined && rawLng !== undefined) hasCoords = true;
            }
          }

          if (hasCoords) continue;

          const parts = [];
          let hasInfoMappings = false;

          Object.keys(stateStore.state.rowMappings).forEach((key) => {
            if (normalizeString(stateStore.state.rowMappings[key]) === 'info') {
              hasInfoMappings = true;
              const cRow = stateStore.state.customRows.find((r) => r.id === key);
              let colName = null;
              if (cRow && cRow.name !== '-') colName = cRow.name;
              else if (key.startsWith('prop_')) colName = key.substring(5);

              if (colName && row[colName]) {
                parts.push(String(row[colName]).trim());
              }
            }
          });

          if (!hasInfoMappings) {
            const systemKeys = [
              'id',
              'lat',
              'lng',
              'matchedFiles',
              'photoMatch',
              '_manuallyAssigned',
              '_localMappings',
            ];
            Object.keys(row).forEach((key) => {
              if (!systemKeys.includes(key) && row[key]) {
                const lKey = key.toLowerCase();
                if (
                  lKey !== 'lat' &&
                  lKey !== 'lng' &&
                  lKey !== 'latitude' &&
                  lKey !== 'longitude'
                ) {
                  parts.push(String(row[key]).trim());
                }
              }
            });
          }

          const searchTarget = parts.filter(Boolean).join(', ');
          if (!searchTarget) continue;

          try {
            const results = await new Promise((resolve) => {
              geocoder.geocode({ address: searchTarget }, (res, status) => {
                if (status === 'OK') resolve(res);
                else if (status === 'OVER_QUERY_LIMIT') {
                  setTimeout(() => {
                    geocoder.geocode(
                      { address: searchTarget },
                      (retryRes, retryStatus) => {
                        if (retryStatus === 'OK') resolve(retryRes);
                        else resolve(null);
                      },
                    );
                  }, 1200);
                } else {
                  resolve(null);
                }
              });
            });

            if (results && results.length > 0) {
              const result = results[0];
              const location = result.geometry.location;
              const nLat = location.lat().toFixed(10);
              const nLng = location.lng().toFixed(10);

              const updatedRow = applyCoordsToRow(row, nLat, nLng, null);
              currentData[i] = updatedRow;
              setters.setParsedData([...currentData]);
            }

            setters.setResolveCoordsState((prev) => ({
              ...prev,
              rowId: row.id,
              searchTarget: searchTarget,
              actualTitle: searchTarget,
            }));
          } catch (e) {}

          await new Promise((res) => setTimeout(res, 400));
        }
      } finally {
        stateStore.refs.cancelProcessRef.current = false;
        setters.setResolveCoordsState((prev) => ({ ...prev, isBulkGenerating: false }));

        const allRows = stateStore.state.parsedData || [];
        if (allRows.length > 0) {
          const firstRow = allRows[0];

          const parts = [];
          let hasInfoMappings = false;

          Object.keys(stateStore.state.rowMappings).forEach((key) => {
            if (normalizeString(stateStore.state.rowMappings[key]) === 'info') {
              hasInfoMappings = true;
              const cRow = stateStore.state.customRows.find((r) => r.id === key);
              let colName = null;
              if (cRow && cRow.name !== '-') colName = cRow.name;
              else if (key.startsWith('prop_')) colName = key.substring(5);

              if (colName && firstRow[colName]) {
                parts.push(String(firstRow[colName]).trim());
              }
            }
          });

          if (!hasInfoMappings) {
            const systemKeys = [
              'id',
              'lat',
              'lng',
              'matchedFiles',
              'photoMatch',
              '_manuallyAssigned',
              '_localMappings',
            ];
            Object.keys(firstRow).forEach((key) => {
              if (!systemKeys.includes(key) && firstRow[key]) {
                const lKey = key.toLowerCase();
                if (
                  lKey !== 'lat' &&
                  lKey !== 'lng' &&
                  lKey !== 'latitude' &&
                  lKey !== 'longitude'
                ) {
                  parts.push(String(firstRow[key]).trim());
                }
              }
            });
          }

          const searchTarget = parts.filter(Boolean).join(', ');

          setters.setResolveCoordsState((prev) => ({
            ...prev,
            rowId: firstRow.id,
            searchTarget: searchTarget,
            actualTitle: searchTarget,
          }));
        } else {
          setters.setResolveCoordsState((prev) => ({
            ...prev,
            rowId: null,
            searchTarget: '',
            actualTitle: '',
            open: false,
          }));
        }
      }
    }
  };

  const handleInvalidMatchOk = (pendingMappingValue) => {
    if (!pendingMappingValue) return;

    const { type, colName, cellValue, rowId } = state.invalidMatchState;
    let newValueName = '';

    if (type === 'REGION') {
      const r = state.regions.find((x) => x.id === pendingMappingValue);
      if (r) newValueName = r.name;
    } else if (type === 'CATEGORY') {
      const c = state.categories.find((x) => x.id === pendingMappingValue);
      if (c) newValueName = c.name;
    } else if (type === 'TEXT') {
      newValueName = pendingMappingValue;
    }

    if (!newValueName) return;

    const otherOccurrences = state.parsedData.filter(
      (row) =>
        row.id !== rowId && normalizeString(row[colName]) === normalizeString(cellValue),
    ).length;

    setters.setParsedData((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          return { ...row, [colName]: newValueName };
        }
        return row;
      }),
    );

    if (otherOccurrences > 0) {
      setTimeout(() => {
        setters.setBulkMatchState({
          open: true,
          rowId,
          colName,
          originalValue: cellValue,
          newValue: newValueName,
          type: type.toLowerCase() === 'text' ? 'text' : type.toLowerCase(),
        });
      }, 100);
    }
  };

  const handleBulkMatchConfirm = () => {
    const { colName, originalValue, newValue } = state.bulkMatchState;
    setters.setParsedData((prev) =>
      prev.map((row) => {
        if (normalizeString(row[colName]) === normalizeString(originalValue)) {
          return { ...row, [colName]: newValue };
        }
        return row;
      }),
    );
    setters.setBulkMatchState((prev) => ({ ...prev, open: false }));
  };

  const handleMissingColumnsConfirm = () => {
    const { missingCols } = state.missingColumnsDialog;

    setters.setColumns((prev) => [...prev, ...missingCols]);
    setters.setParsedData((prev) =>
      prev.map((row) => {
        const newRow = { ...row };
        missingCols.forEach((col) => {
          newRow[col] = '';
        });
        return newRow;
      }),
    );

    setters.setMissingColumnsDialog({ open: false, missingCols: [] });
  };

  return {
    state: {
      fileInputRef: stateStore.refs.fileInputRef,
      viewMode: state.viewMode,
      parsedData: state.parsedData,
      columns: state.columns,
      selectedRowIds: state.selectedRowIds,
      currentFilename: state.currentFilename,
      pendingNextColumn: state.pendingNextColumn,
      userZoomLevel: state.userZoomLevel,
      importHistory: state.importHistory,
      selectedHistoryIds: state.selectedHistoryIds,
      isLoadingHistory: state.isLoadingHistory,
      successfullyImportedIds: state.successfullyImportedIds,
      sessionBatches: state.sessionBatches,
      isPhotoFlowActive: state.isPhotoFlowActive,
      pendingMissingColsCheck: state.pendingMissingColsCheck,
      stagedFiles: state.stagedFiles,
      manualMatchRowId: state.manualMatchRowId,
      documentMatchRowId: docState.documentMatchRowId,
      stagedDocumentFiles: docState.stagedDocumentFiles,
      resolveCoordsState: state.resolveCoordsState,
      invalidMatchState: state.invalidMatchState,
      bulkMatchState: state.bulkMatchState,
      missingColumnsDialog: state.missingColumnsDialog,
      isProcessing: state.isProcessing,
      progressMessage: state.progressMessage,
      uploadProgress: state.uploadProgress || { current: 0, total: 0 },
      isCancelling: state.isCancelling,
      categories: state.categories,
      regions: state.regions,
      globalCategory: state.globalCategory,
      customRows: state.customRows,
      markerIcons: state.markerIcons,
      makeUppercase: state.makeUppercase,
      rteFontType: state.rteFontType,
      rteFontSize: state.rteFontSize,
      imHeadingFontType: state.imHeadingFontType,
      imHeadingFontSize: state.imHeadingFontSize,
      selectedCustomIds: state.selectedCustomIds,
      rowMappings: state.rowMappings,
      rowSubMappings: state.rowSubMappings,
      rowStaticValues: state.rowStaticValues,
      defaultPublicFromDate: state.defaultPublicFromDate,
      publishDateInput: state.publishDateInput,
      savedMenus: state.savedMenus,
      localMenus: state.localMenus,
      selectedMenuId: state.selectedMenuId,
      pendingMenuId: state.pendingMenuId,
      isMenuDirty: state.isMenuDirty,
      isLoadingMenus: state.isLoadingMenus,
      copiedMenuTemplate: state.copiedMenuTemplate,
      alertState: state.alertState,
      confirmDialog: state.confirmDialog,
      showHistoryDeleteConfirm: state.showHistoryDeleteConfirm,
      showHistoryDeleteConfirm2: state.showHistoryDeleteConfirm2,
      showPublishConfirm1: state.showPublishConfirm1,
      showBatchFilenameDialog: state.showBatchFilenameDialog,
      checkedRightIds: state.checkedRightIds,
      triggerDuplicateDelete: state.triggerDuplicateDelete,
      pendingReinstateBatchIds: state.pendingReinstateBatchIds,
      pendingDuplicateDeletions: state.pendingDuplicateDeletions,
      pendingDocumentDeletions: state.pendingDocumentDeletions,
    },
    flags: {
      hasData: effectiveHasData,
      canSave: flags.canSave,
      canDeleteData: flags.canDeleteData,
      canUndoHistory: flags.canUndoHistory,
      isHistoryView: flags.isHistoryView,
    },
    alerts: {
      alertState: state.alertState,
      confirmDialog: state.confirmDialog,
      showHistoryDeleteConfirm: state.showHistoryDeleteConfirm,
      showHistoryDeleteConfirm2: state.showHistoryDeleteConfirm2,
      showPublishConfirm1: state.showPublishConfirm1,
      showBatchFilenameDialog: state.showBatchFilenameDialog,
    },
    actions: {
      setParsedData: setters.setParsedData,
      setColumns: setters.setColumns,
      setCustomRows: setters.setCustomRows,
      setRowMappings: setters.setRowMappings,
      setRowSubMappings: setters.setRowSubMappings,
      setRowStaticValues: setters.setRowStaticValues,
      setStagedFiles: setters.setStagedFiles,
      setManualMatchRowId: setters.setManualMatchRowId,
      setDocumentMatchRowId: docState.setDocumentMatchRowId,
      setStagedDocumentFiles: docState.setStagedDocumentFiles,
      setResolveCoordsState: setters.setResolveCoordsState,
      setInvalidMatchState: setters.setInvalidMatchState,
      setSelectedRowIds: setters.setSelectedRowIds,
      setPendingNextColumn: setters.setPendingNextColumn,
      setUserZoomLevel: setters.setUserZoomLevel,
      handleInvalidMatchOk,
      handleBulkMatchConfirm,
      handleBulkMatchCancel: uiActions.handleBulkMatchCancel,
      handleMissingColumnsConfirm,
      handleMissingColumnsCancel: uiActions.handleMissingColumnsCancel,
      setPublishDateInput: setters.setPublishDateInput,
      setAlertState: setters.setAlertState,
      setConfirmDialog: setters.setConfirmDialog,
      setShowHistoryDeleteConfirm: setters.setShowHistoryDeleteConfirm,
      setShowHistoryDeleteConfirm2: setters.setShowHistoryDeleteConfirm2,
      setShowPublishConfirm1: setters.setShowPublishConfirm1,
      setShowBatchFilenameDialog: setters.setShowBatchFilenameDialog,
      setGlobalCategory: setters.setGlobalCategory,
      handleCustomRowNameChange: uiActions.handleCustomRowNameChange,
      setSelectedCustomIds: setters.setSelectedCustomIds,
      handleNavFirst: uiActions.handleNavFirst,
      handleNavBack: uiActions.handleNavBack,
      handleNavForward: uiActions.handleNavForward,
      handleNavLast: uiActions.handleNavLast,
      handleUploadClick: parser.handleUploadClick,
      handleFileChange: parser.handleFileChange,
      handleCancelClick,
      handleCancelBulkCoords,
      handleReinstateClick,
      handleDeleteClick,
      executeDeleteSettingsOrData,
      executeDeleteBatches: firebase.executeDeleteBatches,
      executeReinstateBatch: reinstate.executeReinstateBatch,
      handlePublishClick,
      executePublishBatches: firebase.executePublishBatches,
      handleSave: handleSaveRequest,
      confirmImportBatch,
      handleSelectAllRows: uiActions.handleSelectAllRows,
      handleSelectRow: uiActions.handleSelectRow,
      handleManualMatch,
      handleDocumentMatch,
      deleteDocumentFromStorage,
      handleResolveCoordinates,
      handleAddCustomRow: uiActions.handleAddCustomRow,
      handleAddDataRow,
      setSelectedHistoryIds: setters.setSelectedHistoryIds,
      setIsPhotoFlowActive: setters.setIsPhotoFlowActive,
      setIsProcessing: setters.setIsProcessing,
      handleCancelProcess: uiActions.handleCancelProcess,
      handleSelectMenu,
      handleMenuTitleChange,
      handleSaveMenu: firebase.handleSaveMenu,
      handleAddNewMenu,
      handleCopyMenu,
      handlePasteMenu: firebase.handlePasteMenu,
      setIsMenuDirty: setters.setIsMenuDirty,
      setCheckedRightIds: setters.setCheckedRightIds,
      setTriggerDuplicateDelete: setters.setTriggerDuplicateDelete,
      setPendingDuplicateDeletions: setters.setPendingDuplicateDeletions,
      handleDeleteColumnClick,
      docState,
    },
  };
};
